import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useData } from '../context/DataContext';
import StripePayment from './StripePayment';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiDollarSign, FiCalendar, FiCreditCard, FiCheck, FiInfo } = FiIcons;

const PaymentModal = ({ student, onClose }) => {
  const { sessions, formatCurrency, calculateRemainingBalance, getParentById, appSettings } = useData();
  const [activeGateway, setActiveGateway] = useState(null);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [paymentType, setPaymentType] = useState('sessions');
  const [customAmount, setCustomAmount] = useState('');
  const [stripeEnabled, setStripeEnabled] = useState(false);

  useEffect(() => {
    const checkConfig = async () => {
      const stripeDoc = await getDoc(doc(db, 'paymentConfig', 'stripe'));
      setStripeEnabled(stripeDoc.exists() && !!stripeDoc.data().publicKey);
    };
    checkConfig();
  }, []);

  const getUnpaidSessions = () => {
    const unpaid = [];
    const studentSessions = sessions[student.id] || {};
    Object.entries(studentSessions).forEach(([month, monthSessions]) => {
      monthSessions.forEach(session => {
        if (!session.isPaid) {
          unpaid.push({
            month,
            session,
            key: `${student.id}-${month}-${session.id}`
          });
        }
      });
    });
    return unpaid;
  };

  const unpaidSessionsList = getUnpaidSessions();

  const toggleSession = (key) => {
    setSelectedSessions(prev => 
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const calculatePaymentAmount = () => {
    if (paymentType === 'sessions') {
      return selectedSessions.reduce((total, key) => {
        const item = unpaidSessionsList.find(s => s.key === key);
        return total + parseFloat(item?.session?.feePerSession || 0);
      }, 0);
    }
    if (paymentType === 'balance') return calculateRemainingBalance(student.id);
    if (paymentType === 'custom') return parseFloat(customAmount) || 0;
    return 0;
  };

  const finalAmount = calculatePaymentAmount();

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-40 backdrop-blur-sm">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-[2.5rem] shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-8 border-b border-gray-100">
          <div>
            <h2 className="text-2xl font-black text-gray-900 tracking-tight uppercase">Billing Portal</h2>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">{student.studentName} • Grade {student.grade}</p>
          </div>
          <button onClick={onClose} className="p-4 hover:bg-gray-100 rounded-2xl text-gray-400"><SafeIcon icon={FiX} className="text-xl" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-8 space-y-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
               <h3 className="text-xs font-black text-indigo-600 uppercase tracking-widest flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-indigo-600 rounded-full"></div> 1. Select Payment Method
              </h3>
              <div className="grid grid-cols-3 gap-3">
                {['sessions', 'balance', 'custom'].map(type => (
                  <button key={type} onClick={() => setPaymentType(type)} className={`p-4 rounded-2xl border-2 flex flex-col items-center gap-2 transition-all ${paymentType === type ? 'border-indigo-600 bg-indigo-50 shadow-lg' : 'border-gray-100 hover:border-gray-200'}`}>
                    <SafeIcon icon={type === 'sessions' ? FiCalendar : type === 'balance' ? FiDollarSign : FiCreditCard} className={paymentType === type ? 'text-indigo-600 text-xl' : 'text-gray-300 text-xl'} />
                    <span className="text-[9px] font-black uppercase tracking-widest">{type}</span>
                  </button>
                ))}
              </div>

              {paymentType === 'sessions' && (
                <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                  {unpaidSessionsList.length > 0 ? unpaidSessionsList.map(item => (
                    <button key={item.key} onClick={() => toggleSession(item.key)} className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${selectedSessions.includes(item.key) ? 'border-indigo-600 bg-indigo-50' : 'border-gray-50 bg-gray-50'}`}>
                      <div className="text-left">
                        <p className="text-xs font-black text-gray-900 uppercase">{item.session.subject}</p>
                        <p className="text-[10px] font-bold text-gray-400">{new Date(item.session.date).toLocaleDateString()}</p>
                      </div>
                      <span className="text-xs font-black text-indigo-600">{formatCurrency(item.session.feePerSession)}</span>
                    </button>
                  )) : (
                    <p className="text-xs font-bold text-gray-400 text-center py-4 bg-gray-50 rounded-xl">No unpaid sessions found.</p>
                  )}
                </div>
              )}

              {paymentType === 'custom' && (
                <input type="number" value={customAmount} onChange={(e) => setCustomAmount(e.target.value)} placeholder="Enter amount..." className="w-full p-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none font-bold" />
              )}
            </div>

            <div className="bg-gray-900 rounded-[2rem] p-10 text-white flex flex-col justify-center relative overflow-hidden">
              <SafeIcon icon={FiDollarSign} className="absolute -bottom-6 -right-6 text-9xl text-white/5" />
              <p className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.2em] mb-2">Total to Pay</p>
              <h4 className="text-5xl font-black tracking-tighter">{formatCurrency(finalAmount)}</h4>
              <p className="text-[10px] font-bold text-gray-400 mt-4 uppercase tracking-widest">
                {paymentType === 'sessions' ? `${selectedSessions.length} Sessions selected` : 'Lump sum payment'}
              </p>
            </div>
          </div>

          <div className="pt-8 border-t border-gray-100">
            <h3 className="text-xs font-black text-indigo-600 uppercase tracking-widest flex items-center gap-2 mb-6">
              <div className="w-1.5 h-1.5 bg-indigo-600 rounded-full"></div> 2. Pay via Stripe
            </h3>
            {stripeEnabled ? (
              <button 
                onClick={() => setActiveGateway('stripe')} 
                disabled={finalAmount <= 0}
                className="w-full flex items-center justify-between p-8 border-2 border-gray-100 rounded-[2rem] hover:border-indigo-600 hover:bg-indigo-50 transition-all group disabled:opacity-30"
              >
                <div className="flex items-center gap-6">
                  <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center text-white shadow-xl shadow-indigo-200">
                    <SafeIcon icon={FiCreditCard} className="text-2xl" />
                  </div>
                  <div className="text-left">
                    <p className="text-xl font-black text-gray-900">Secure Stripe Payment</p>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Supports all major credit & debit cards</p>
                  </div>
                </div>
                <SafeIcon icon={FiIcons.FiArrowRight} className="text-2xl text-gray-200 group-hover:text-indigo-600 group-hover:translate-x-2 transition-all" />
              </button>
            ) : (
              <div className="bg-rose-50 p-6 rounded-2xl border-2 border-rose-100 flex items-center gap-4">
                <SafeIcon icon={FiInfo} className="text-rose-500 text-xl" />
                <p className="text-xs font-black text-rose-700 uppercase tracking-widest">Stripe is not configured by the administrator.</p>
              </div>
            )}
          </div>
        </div>

        {activeGateway === 'stripe' && (
          <StripePayment 
            amount={finalAmount} 
            currency={appSettings.currency}
            onClose={() => setActiveGateway(null)}
            paymentDetails={{
              studentId: student.id,
              studentName: student.studentName,
              paymentType,
              sessionKeys: selectedSessions
            }}
          />
        )}
      </motion.div>
    </div>
  );
};

export default PaymentModal;